/**
 * Rivers — swaps each decorative .river div's background for an inline copy of
 * the same artwork, which is what makes its <animate> actually run.
 *
 * An SVG loaded as an image does not animate itself. Scripts are the well known
 * casualty, but SMIL goes with them: the same file that morphs happily inline
 * sits frozen on its first frame as a background-image (and in an <img>). So
 * the mapping stays in CSS — sections.css points each river at its -anim.svg
 * behind prefers-reduced-motion — and this reads whatever the cascade landed
 * on, fetches it, and drops it in as a child.
 *
 * Every failure leaves the background in place, showing the first state, which
 * is a perfectly good river: no JS, a failed fetch, something that is not an
 * SVG, reduced motion, or a viewport under the breakpoint where .river is
 * display:none anyway (no point pulling 20-40KB for something nobody sees).
 *
 * overflow is forced closed on the inline copy. Four of these files carry
 * Figma's overflow="visible", which means nothing to a background-image — the
 * viewBox is the whole canvas there — but inline it would let artwork outside
 * the box paint, and river-2 for one runs 19px past its own left edge.
 *
 * Paused off screen. These are 230-380 point paths morphing across boxes up to
 * 800x1900; repainting one nobody is looking at is pure waste.
 */

const WIDE = '(min-width: 1024px)';

function fit(svg: SVGSVGElement): void {
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  // keep the file's own preserveAspectRatio: the div carries a matching
  // aspect-ratio, so this renders exactly as `background-size: contain` did
  svg.style.display = 'block';
  svg.style.overflow = 'hidden';
}

async function inline(el: HTMLElement, io: IntersectionObserver): Promise<void> {
  const url = getComputedStyle(el).backgroundImage.match(/url\(["']?(.*?)["']?\)/)?.[1];
  if (!url || !url.includes('-anim')) return;

  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'image/svg+xml');
    const svg = doc.documentElement as unknown as SVGSVGElement;
    if (svg.nodeName !== 'svg' || doc.querySelector('parsererror')) return;

    fit(svg);
    el.textContent = '';
    el.appendChild(svg);
    el.style.backgroundImage = 'none';
    svg.pauseAnimations();
    io.observe(el);
  } catch {
    /* leave the background alone */
  }
}

export default function rivers(): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const els = Array.from(document.querySelectorAll<HTMLElement>('.river'));
  if (!els.length) return;

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const svg = entry.target.querySelector('svg');
        if (!svg) return;
        if (entry.isIntersecting) svg.unpauseAnimations();
        else svg.pauseAnimations();
      });
    },
    // start it a screen early so a river is already flowing when it arrives
    { rootMargin: '100% 0px' }
  );

  const wide = window.matchMedia(WIDE);
  const run = () => {
    if (!wide.matches) return;
    els.forEach((el) => {
      if (!el.firstElementChild) inline(el, io);
    });
  };

  run();
  wide.addEventListener('change', run);
}
